import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Download, X, Smartphone, CheckCircle, Scan, Shield, Bell } from 'lucide-react';

export const PWAInstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  const [showIOSGuide, setShowIOSGuide] = useState(false);

  useEffect(() => {
    // Check if already installed
    const standalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;
    setIsStandalone(standalone);

    // Check if iOS
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    setIsIOS(iOS);

    // Listen for install prompt
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      
      // Show prompt after a delay if not dismissed before
      const dismissed = localStorage.getItem('pwa-prompt-dismissed');
      const lastDismissed = localStorage.getItem('pwa-prompt-dismissed-time');
      
      // Show again after 7 days
      const sevenDays = 7 * 24 * 60 * 60 * 1000;
      const canShowAgain = !lastDismissed || (Date.now() - parseInt(lastDismissed)) > sevenDays;
      
      if (!dismissed || canShowAgain) {
        setTimeout(() => setShowPrompt(true), 3000);
      }
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);

    // Show iOS instructions after delay
    if (iOS && !standalone) {
      const dismissed = localStorage.getItem('pwa-prompt-dismissed');
      const lastDismissed = localStorage.getItem('pwa-prompt-dismissed-time');
      const sevenDays = 7 * 24 * 60 * 60 * 1000;
      const canShowAgain = !lastDismissed || (Date.now() - parseInt(lastDismissed)) > sevenDays;
      
      if (!dismissed || canShowAgain) {
        setTimeout(() => setShowPrompt(true), 5000);
      }
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
    };
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setShowPrompt(false);
        localStorage.removeItem('pwa-prompt-dismissed');
        localStorage.removeItem('pwa-prompt-dismissed-time');
      }
      setDeferredPrompt(null);
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem('pwa-prompt-dismissed', 'true');
    localStorage.setItem('pwa-prompt-dismissed-time', Date.now().toString());
  };

  const handleRemindLater = () => {
    setShowPrompt(false);
    // Will show again in 24 hours
    localStorage.setItem('pwa-prompt-dismissed-time', (Date.now() - (6 * 24 * 60 * 60 * 1000)).toString());
  };

  if (isStandalone || !showPrompt) return null;

  // Expanded iOS guide view
  if (isIOS && showIOSGuide) {
    return (
      <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-4">
        <Card className="w-full max-w-md shadow-2xl border-[#5DBDB3]/30 bg-white dark:bg-slate-900 animate-slide-up">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-lg">Install SEEN App</h3>
              <button onClick={() => setShowIOSGuide(false)} className="text-muted-foreground hover:text-foreground">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="space-y-4">
              <div className="flex items-start gap-3 p-3 bg-muted rounded-lg">
                <div className="w-8 h-8 rounded-full bg-[#5DBDB3] text-white flex items-center justify-center text-sm font-bold shrink-0">1</div>
                <div>
                  <p className="font-medium text-sm">Tap the Share button</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Look for <span className="inline-flex items-center px-1.5 py-0.5 bg-background rounded text-[10px] font-medium">
                      <svg className="w-3.5 h-3.5 mr-0.5" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M15 8a.5.5 0 01.5.5v6a.5.5 0 01-1 0v-6A.5.5 0 0115 8z"/>
                        <path d="M10 .5a.5.5 0 01.5.5v12a.5.5 0 01-1 0V1a.5.5 0 01.5-.5z"/>
                        <path d="M10 0l4 4H6l4-4z"/>
                        <rect x="2" y="12" width="16" height="6" rx="1"/>
                      </svg>
                      Share
                    </span> at the bottom of Safari
                  </p>
                </div>
              </div>
              
              <div className="flex items-start gap-3 p-3 bg-muted rounded-lg">
                <div className="w-8 h-8 rounded-full bg-[#5DBDB3] text-white flex items-center justify-center text-sm font-bold shrink-0">2</div>
                <div>
                  <p className="font-medium text-sm">Scroll and tap &quot;Add to Home Screen&quot;</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    You may need to scroll down to find this option
                  </p>
                </div>
              </div>
              
              <div className="flex items-start gap-3 p-3 bg-muted rounded-lg">
                <div className="w-8 h-8 rounded-full bg-[#5DBDB3] text-white flex items-center justify-center text-sm font-bold shrink-0">3</div>
                <div>
                  <p className="font-medium text-sm">Tap &quot;Add&quot; to confirm</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    SEEN will appear on your home screen like a native app
                  </p>
                </div>
              </div>
            </div>
            
            <Button 
              onClick={handleDismiss} 
              variant="outline" 
              className="w-full mt-4"
            >
              Got it, thanks!
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="fixed bottom-20 left-4 right-4 z-50 animate-slide-up sm:left-auto sm:right-4 sm:max-w-sm">
      <Card className="shadow-2xl border-[#5DBDB3]/30 bg-white dark:bg-slate-900">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#5DBDB3] to-[#4AA89E] flex items-center justify-center shrink-0 shadow-lg">
              <Smartphone className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-2">
                <h3 className="font-semibold text-sm">Install SEEN App</h3>
                <button onClick={handleDismiss} className="text-muted-foreground hover:text-foreground -mt-1">
                  <X className="w-4 h-4" />
                </button>
              </div>
              
              {/* Benefits list */}
              <div className="mt-2 space-y-1">
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <Scan className="w-3 h-3 text-[#5DBDB3]" />
                  <span>Quick barcode scanning</span>
                </div>
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <Shield className="w-3 h-3 text-[#5DBDB3]" />
                  <span>Instant allergen alerts</span>
                </div>
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <Bell className="w-3 h-3 text-[#5DBDB3]" />
                  <span>Works offline</span>
                </div>
              </div>
              
              {isIOS ? (
                <Button 
                  onClick={() => setShowIOSGuide(true)} 
                  size="sm" 
                  className="mt-3 w-full bg-[#5DBDB3] hover:bg-[#4AA89E] h-9 text-xs"
                >
                  <Download className="w-3 h-3 mr-1.5" />
                  Show Me How
                </Button>
              ) : (
                <div className="flex gap-2 mt-3">
                  <Button 
                    onClick={handleRemindLater} 
                    size="sm" 
                    variant="ghost"
                    className="flex-1 h-8 text-xs"
                  >
                    Later
                  </Button>
                  <Button 
                    onClick={handleInstall} 
                    size="sm" 
                    className="flex-1 bg-[#5DBDB3] hover:bg-[#4AA89E] h-8 text-xs"
                  >
                    <Download className="w-3 h-3 mr-1" />
                    Install
                  </Button>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PWAInstallPrompt;
